import {
  Box,
  Button,
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
} from '@amzn/awsui-components-core';
import arraySort from 'array-sort';
import { ContentRequest } from '@amzn/awscat-pattern-library-service-model';
import React, { useState } from 'react';
import { deepClone, getHeaderCounterText, tablePaginationAriaLabels } from '../../api/UtilsApi';
import constants from '../../common/constants';
import LocalStorage from '../../common/LocalStorage';
import TableSettingOptions from '../../model/TableSettingOptions';
import { TableEmptyState, TableNoMatchState } from '../common/CommonComponents';
import {
  DefaultContentRequestSelectorOptions,
  DefaultContentRequestSortableColumns,
} from '../content-requests/ContentRequestColumnDefinition';
import { DefaultTablePageSizeOptions } from '../workflow-viewer/ContentWorkflowColumnDefinitions';
import { NonCancelableCustomEvent } from '@amzn/awsui-components-core';
import { useCollection } from '@amzn/awsui-collection-hooks';

interface ContentRequestTableProp {
  tableId: string;
  headerText?: string;
  contentRequests: ContentRequest[];
  columnDefinitions: Array<TableProps.ColumnDefinition<ContentRequest>>;
  onSelectionChange?: (selectedContentRequests: ContentRequest[]) => void;
  onRefreshClick?: (condition?: (contentRequests: ContentRequest[]) => boolean) => void;
  loading?: boolean;
  selectionType?: TableProps.SelectionType;
}

const ContentRequestTable: React.FunctionComponent<ContentRequestTableProp> = (
  props: ContentRequestTableProp,
) => {
  const { columnDefinitions, contentRequests, tableId, headerText, loading, onRefreshClick } =
    props;
  const contentRequestItems = arraySort(contentRequests, 'createdDate', { reverse: true });

  const contentSelectorOptions: CollectionPreferencesProps.ContentDisplayOption[] =
    DefaultContentRequestSelectorOptions.filter((defaultOption) =>
      columnDefinitions.some((columnDef) => columnDef.id === defaultOption.id),
    ).map((option) => {
      const content: CollectionPreferencesProps.ContentDisplayOption = {
        id: option.id,
        label: option.label,
        alwaysVisible: !option.editable,
      };
      return content;
    });

  const initializeTablePreferenceCache = (): TableSettingOptions => {
    const existingTablePreferenceCache = LocalStorage.getTableSettingOptions(tableId);
    if (existingTablePreferenceCache?.visibleColumns?.size > 0) {
      return existingTablePreferenceCache;
    } else {
      return Object.assign(existingTablePreferenceCache, {
        ...contentSelectorOptions,
        visibleColumns: new Set(contentSelectorOptions.map((column) => column.id ?? '')),
      });
    }
  };
  const columnDefinitionsWithSorting = columnDefinitions.map((column) => {
    const sortingCol = DefaultContentRequestSortableColumns.find(
      (sortCol) => sortCol.id === column.id,
    );
    if (sortingCol) {
      column.sortingComparator = sortingCol.comparator;
    }
    return column;
  });
  const [tablePreferenceCache, setTablePreferenceCache] = useState<TableSettingOptions>(
    initializeTablePreferenceCache(),
  );
  const [tablePreferences, setTablePreferences] = useState<TableSettingOptions>(
    deepClone(tablePreferenceCache),
  );
  const [selectedItems, setSelectedItems] = useState<ContentRequest[]>([]);
  const [contentDescriptionGroup, setContentDescriptionGroup] =
    useState<CollectionPreferencesProps.ContentDisplayOption[]>(contentSelectorOptions);
  const [columnContentDisplay, setColumnContentDisplay] = useState<
    CollectionPreferencesProps.ContentDisplayItem[]
  >(
    contentDescriptionGroup.map((content) => {
      return {
        id: content.id,
        visible: content.alwaysVisible || tablePreferenceCache.visibleColumns.has(content.id),
      };
    }, []),
  );
  const visibleContentPreference: CollectionPreferencesProps.ContentDisplayPreference = {
    title: constants.TABLE_CONTENT_SELECTOR_TITLE,
    options: contentDescriptionGroup,
  };
  const { pageSize, stickyHeader, wrapLines } = tablePreferences;

  const handleRefresh = (event: any, condition?: any) => {
    if (onRefreshClick) {
      onRefreshClick(condition);
    }
  };

  const clearFilter = () => {
    return;
  };

  const changeTableSetting = (
    e: NonCancelableCustomEvent<CollectionPreferencesProps.Preferences<boolean>>,
  ) => {
    const { pageSize: size, wrapLines: wrapLinesFlag, custom, contentDisplay } = e.detail;
    const updatedTablePreferenceCache = tablePreferenceCache;
    if (contentDisplay !== undefined) {
      updatedTablePreferenceCache.visibleColumns = new Set(
        contentDisplay?.filter((content) => !!content.visible).map((content) => content.id),
      );
      setColumnContentDisplay([...(contentDisplay ?? [])]);
      setContentDescriptionGroup(contentSelectorOptions);
    }
    if (size !== undefined) {
      updatedTablePreferenceCache.pageSize = size;
    }
    if (custom !== undefined) {
      updatedTablePreferenceCache.stickyHeader = custom;
    }
    if (wrapLinesFlag !== undefined) {
      updatedTablePreferenceCache.wrapLines = wrapLinesFlag;
    }

    LocalStorage.setTableSettingOptions(tableId, updatedTablePreferenceCache);
    setTablePreferenceCache(updatedTablePreferenceCache);
    setTablePreferences(deepClone(updatedTablePreferenceCache));
  };

  const header = (
    <Header
      variant="h3"
      actions={
        <Button
          variant="icon"
          iconName="refresh"
          ariaLabel="Refresh table"
          onClick={handleRefresh}
        />
      }
      counter={`${getHeaderCounterText(contentRequestItems, selectedItems)}`}
    >
      {`${headerText || 'Total'} `}
    </Header>
  );

  const tableEmptyState =
    Array.isArray(contentRequestItems) && contentRequestItems.length ? (
      <TableNoMatchState onClearFilter={clearFilter} />
    ) : (
      <TableEmptyState />
    );

  const preferences: CollectionPreferencesProps.Preferences<boolean> = {
    pageSize,
    wrapLines,
    contentDisplay: columnContentDisplay,
    custom: stickyHeader,
  };

  const customPreference = (customValue: boolean, setCustomValue: React.Dispatch<boolean>) => (
    <Checkbox checked={customValue} onChange={({ detail }) => setCustomValue(detail.checked)}>
      Sticky header
    </Checkbox>
  );

  const collectionPreferences = (
    <Box padding={{ bottom: 's' }}>
      <CollectionPreferences
        title={constants.PREFERENCES}
        confirmLabel={constants.CONFIRM_BUTTON_TEXT}
        cancelLabel={constants.CANCEL_BUTTON_TEXT}
        preferences={preferences}
        onConfirm={changeTableSetting}
        pageSizePreference={{
          title: constants.TABLE_PAGE_SELECTOR_MESSAGE,
          options: DefaultTablePageSizeOptions,
        }}
        wrapLinesPreference={{
          label: constants.TABLE_WRAPLINES_LABEL,
          description: constants.TABLE_WRAPLINES_DESCRIPTION,
        }}
        contentDisplayPreference={visibleContentPreference}
        customPreference={customPreference}
      />
    </Box>
  );

  const { items, collectionProps, paginationProps } = useCollection(contentRequestItems, {
    filtering: {
      empty: 'No contents',
    },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: 'createdDate' },
        isDescending: true,
      },
    },
    pagination: { pageSize: preferences.pageSize },
  });

  return (
    <Table
      {...collectionProps}
      loading={!!loading}
      header={header}
      empty={tableEmptyState}
      columnDefinitions={columnDefinitionsWithSorting}
      items={items}
      wrapLines={!!wrapLines}
      stickyHeader={!!stickyHeader}
      resizableColumns
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      selectedItems={selectedItems}
      columnDisplay={columnContentDisplay}
      preferences={collectionPreferences}
      pagination={<Pagination {...paginationProps} ariaLabels={tablePaginationAriaLabels} />}
    />
  );
};

export default ContentRequestTable;
